// src/pages/Performance.jsx
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Eye, DollarSign, Target, BarChart3, 
  Calendar, Download, Filter, ArrowUpRight, ArrowDownRight,
  Package, Users, Clock, Award, Zap
} from 'lucide-react';
import PageLayout from '../../components/layout/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { getSellerAnalytics } from '../../services/api';
import Loading from '../../components/common/Loading';

// Mock chart component - replace with real chart library like Recharts
const PerformanceChart = ({ data, type = 'line' }) => {
  return (
    <div className="h-64 bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">Revenue Over Time</h4>
        <span className="text-sm text-green-600 flex items-center">
          <ArrowUpRight className="w-4 h-4 mr-1" />
          +12.5%
        </span>
      </div>
      <div className="h-48 flex items-end space-x-1">
        {[40, 65, 50, 80, 60, 90, 70, 85, 75, 95, 80, 100].map((height, index) => (
          <div
            key={index}
            className="flex-1 flex flex-col items-center"
          >
            <div
              className="w-full rounded-t-lg bg-gradient-to-t from-[#ff660e] to-orange-400"
              style={{ height: `${height}%` }}
            />
            <span className="text-xs text-gray-500 mt-2">
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Performance = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('30days');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchPerformanceStats();
    }
  }, [user, timeRange]);

  const fetchPerformanceStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSellerAnalytics(user.user_id, timeRange);
      
      if (response.success) {
        setStats(response.data);
      } else {
        setError('Failed to load performance data');
      }
    } catch (err) {
      // console.error('Error fetching performance stats:', err);
      setError('Failed to load performance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const timeRanges = [
    { id: '7days', label: 'Last 7 Days' },
    { id: '30days', label: 'Last 30 Days' },
    { id: '90days', label: 'Last 90 Days' },
    { id: 'year', label: 'This Year' },
  ];

  if (loading) {
    return (
      <PageLayout title="Performance Analytics">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loading />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Performance Analytics" 
      subtitle="Track your sales and growth"
      rightAction={
        <button className="flex items-center px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </button>
      }
    >
      {/* Time Range Selector */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div className="mb-4 md:mb-0">
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Performance Overview</h3>
            <p className="text-gray-600 text-sm">Track your sales performance and growth metrics</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff660e]"
            >
              {timeRanges.map(range => (
                <option key={range.id} value={range.id}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm text-green-600 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              +15%
            </span>
          </div>
          <h4 className="text-2xl font-bold text-[#0f0e0e] mb-2">
            ₦{(stats?.totalRevenue || 1250000).toLocaleString()}
          </h4>
          <p className="text-gray-600">Total Revenue</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm text-green-600 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              +8%
            </span>
          </div>
          <h4 className="text-2xl font-bold text-[#0f0e0e] mb-2">{stats?.totalSales || 42}</h4>
          <p className="text-gray-600">Total Sales</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-sm text-green-600 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              +24%
            </span>
          </div>
          <h4 className="text-2xl font-bold text-[#0f0e0e] mb-2">{stats?.totalViews || 1250}</h4>
          <p className="text-gray-600">Total Views</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl">
              <Target className="w-6 h-6 text-[#ff660e]" />
            </div>
            <span className="text-sm text-green-600 flex items-center">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              +3.2%
            </span>
          </div>
          <h4 className="text-2xl font-bold text-[#0f0e0e] mb-2">
            {(stats?.conversionRate || 3.4).toFixed(1)}%
          </h4>
          <p className="text-gray-600">Conversion Rate</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Revenue Trend</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <PerformanceChart type="line" />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Views vs Sales</h3>
            <Target className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <div className="w-40 h-40 mx-auto mb-4 relative">
                {/* Pie chart placeholder */}
                <div className="absolute inset-0 rounded-full border-8 border-blue-500"></div>
                <div className="absolute inset-0 rounded-full border-8 border-green-500 transform -rotate-45"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{stats?.conversionRate || 3.4}%</div>
                    <div className="text-sm text-gray-600">Conversion</div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center space-x-6">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span className="text-sm">Views</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm">Sales</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Listings */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Top Performing Listings</h3>
            <p className="text-gray-600 text-sm">Your best selling products</p>
          </div>
          <Filter className="w-5 h-5 text-gray-400" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Product</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Views</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Sales</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Revenue</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Conversion</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.topListings || [
                { id: 1, title: 'iPhone 13 Pro', views: 450, sales: 15, revenue: 450000, conversion: 3.3, status: 'active' },
                { id: 2, title: 'Designer Handbag', views: 320, sales: 12, revenue: 360000, conversion: 3.8, status: 'active' },
                { id: 3, title: 'Gaming Laptop', views: 280, sales: 8, revenue: 320000, conversion: 2.9, status: 'sold' },
                { id: 4, title: 'Smart Watch', views: 190, sales: 6, revenue: 180000, conversion: 3.2, status: 'active' },
                { id: 5, title: 'Camera DSLR', views: 150, sales: 4, revenue: 200000, conversion: 2.7, status: 'active' },
              ]).map((item) => (
                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg mr-3"></div>
                      <span className="font-medium">{item.title}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">{item.views}</td>
                  <td className="py-4 px-4">{item.sales}</td>
                  <td className="py-4 px-4 font-medium">₦{item.revenue.toLocaleString()}</td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.conversion > 3 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.conversion}%
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {item.status === 'active' ? 'Active' : 'Sold'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights & Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
          <div className="flex items-center mb-4">
            <Award className="w-6 h-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Performance Insights</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center">
              <ArrowUpRight className="w-4 h-4 text-green-500 mr-2" />
              <span className="text-gray-700">Your conversion rate is 15% above average</span>
            </li>
            <li className="flex items-center">
              <Clock className="w-4 h-4 text-yellow-500 mr-2" />
              <span className="text-gray-700">Best selling time: Weekdays 2-5 PM</span>
            </li>
            <li className="flex items-center">
              <Package className="w-4 h-4 text-[#ff660e] mr-2" />
              <span className="text-gray-700">Electronics category brings 40% of revenue</span>
            </li>
          </ul>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border border-orange-100">
          <div className="flex items-center mb-4">
            <Target className="w-6 h-6 text-[#ff660e] mr-3" />
            <h3 className="text-lg font-semibold text-[#0f0e0e]">Recommendations</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center">
              <Zap className="w-4 h-4 text-yellow-500 mr-2" />
              <span className="text-gray-700">Boost your top 3 listings for 30% more visibility</span>
            </li>
            <li className="flex items-center">
              <Users className="w-4 h-4 text-purple-500 mr-2" />
              <span className="text-gray-700">Respond to inquiries within 1 hour to increase sales</span>
            </li>
            <li className="flex items-center">
              <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
              <span className="text-gray-700">Add more photos to increase conversion by 25%</span>
            </li>
          </ul>
        </div>
      </div>
    </PageLayout>
  );
};

export default Performance;